import { createContext, PropsWithChildren, useContext, useState } from 'react';

import { text4 } from '@osmosis/tailwind-ui/styles/text';
import { faCommentExclamation } from '@fortawesome/pro-solid-svg-icons/faCommentExclamation';
import { useIntl } from 'react-intl';
import { ids, path } from '../components/quiz/quizSettingsPopup/utils';
import { useQuizContext } from './QuizContext';
import { useQuizShortcutsContext } from './QuizShortcutsContext';
import { useUserContext } from './UserContext';
import useModal, { ModalHookProps } from '../hooks/useModal';
import { useSearchContext } from './SearchProvider';
import { shortcutsIds } from '../constants/quizShortcuts';
import { QuizConfidence } from '../types/quiz';
import { PopupMenuItem } from '../components/PopupMenu';

type SetOnCorrectnessKeyHandler = (isCorrect: boolean) => void;
type SetOnConfidenceKeyHandler = (confidence: QuizConfidence) => void;

type QuizItemsContextValues = {
  items: PopupMenuItem[];
  feedbackModal: ModalHookProps;
  hotkeyMap: { [index: string]: string };
  hotkeyHandlers: {
    [index: string]: (e?: KeyboardEvent) => void;
  };
  setOnConfidenceKeyHandler: React.Dispatch<
    React.SetStateAction<SetOnConfidenceKeyHandler | undefined>
  >;

  setOnCorrectnessKeyHandler: React.Dispatch<
    React.SetStateAction<SetOnCorrectnessKeyHandler | undefined>
  >;
};

const QuizItemsContext = createContext<QuizItemsContextValues>({
  items: [],
  feedbackModal: {
    show: () => {
      // initial
    },
    hide: () => {
      // initial
    },
    toggle: () => {
      // initial
    },
    setIsVisible: () => {
      // initial
    },
    isVisible: false,
    data: null,
  },
  hotkeyMap: {},
  hotkeyHandlers: {},
  setOnConfidenceKeyHandler: () => {
    /** */
  },
  setOnCorrectnessKeyHandler: () => {
    /** */
  },
});

type QuizItemsProps = Record<string, unknown>;

/**
 * QuizItemsProvider.
 */
const QuizItemsProvider = (
  props: PropsWithChildren<QuizItemsProps>,
): JSX.Element => {
  const { children } = props;

  const intl = useIntl();
  const {
    currentItem,
    changeItem,
    handleReschedule,
    handleFlag,
    handleUnschedule,
  } = useQuizContext();
  const { user } = useUserContext();
  const { showKeyboardShortcuts, shortcutsEnabled } = useQuizShortcutsContext();
  const { isSearchFieldFocused } = useSearchContext();
  const feedbackModal = useModal();

  // Register handlers from other components
  const [onConfidenceKeyHandler, setOnConfidenceKeyHandler] =
    useState<(confidence: QuizConfidence) => void>();
  const [onCorrectnessKeyHandler, setOnCorrectnessKeyHandler] =
    useState<(isCorrect: boolean) => void>();

  const { shortcuts } = useQuizShortcutsContext();

  const hotkeysEnabled =
    shortcutsEnabled &&
    !isSearchFieldFocused &&
    !feedbackModal.isVisible &&
    !showKeyboardShortcuts;
  const isEditor = user?.admin?.editor;

  const items: PopupMenuItem[] = [
    {
      id: ids.report,
      text: intl.formatMessage({
        id: 'quiz.quiz-settings-popup.report',
        defaultMessage: 'Report an issue',
      }),
      icon: faCommentExclamation,
      iconColor: text4,
      onClick: () => {
        feedbackModal.show();
      },
    },
  ];

  if (isEditor) {
    items.push({
      id: ids.admin,
      text: intl.formatMessage(
        {
          id: 'quiz.quiz-settings-popup.admin',
          defaultMessage: 'Admin edit {nodeID}',
        },
        { nodeID: currentItem?.nodeID },
      ),
      onClick: () => {
        window.open(path.editques(currentItem?.nodeID || -1), '_blank');
      },
    });
  }

  let hotkeyMap: { [index: string]: string } = {};
  if (hotkeysEnabled) {
    hotkeyMap = shortcuts;
  }

  const hotkeyHandlers = {
    [shortcutsIds.prev]: (e?: KeyboardEvent) => {
      e?.preventDefault();
      changeItem(-1);
    },
    [shortcutsIds.next]: (e?: KeyboardEvent) => {
      e?.preventDefault();
      changeItem(1);
    },
    [shortcutsIds.skip]: (e?: KeyboardEvent) => {
      const popupContainer = document.querySelector(
        'div[data-testid="popup-container"]',
      );

      const activeElementIsImage =
        document.activeElement?.tagName === 'lightbox-image';

      const activeElementIsSettingsButton =
        document.activeElement?.getAttribute('data-testid') === 'settings';

      const activeElementIsAnswerChoiceOption =
        document.activeElement?.getAttribute('data-testid') ===
        'answer-choice-option';

      const activeElementIsAnswerCheckboxOption =
        document.activeElement?.getAttribute('data-testid') ===
        'answer-checkbox-option';

      if (
        activeElementIsImage ||
        activeElementIsAnswerCheckboxOption ||
        activeElementIsAnswerChoiceOption
      ) {
        return;
      } else if (!activeElementIsSettingsButton && !popupContainer) {
        e?.preventDefault();
        changeItem(1);
      }
    },
    ...(onConfidenceKeyHandler && {
      [shortcutsIds.high]: (e?: KeyboardEvent) => {
        e?.preventDefault();
        onConfidenceKeyHandler?.(QuizConfidence.HIGH);
      },
      [shortcutsIds.medium]: (e?: KeyboardEvent) => {
        e?.preventDefault();
        onConfidenceKeyHandler?.(QuizConfidence.MEDIUM);
      },
      [shortcutsIds.low]: (e?: KeyboardEvent) => {
        e?.preventDefault();
        onConfidenceKeyHandler?.(QuizConfidence.LOW);
      },
    }),
    ...(onCorrectnessKeyHandler && {
      [shortcutsIds.correct]: (e?: KeyboardEvent) => {
        e?.preventDefault();
        onCorrectnessKeyHandler?.(true);
      },
      [shortcutsIds.incorrect]: (e?: KeyboardEvent) => {
        e?.preventDefault();
        onCorrectnessKeyHandler?.(false);
      },
    }),
    [shortcutsIds['1m']]: (e?: KeyboardEvent) => {
      e?.preventDefault();
      handleReschedule(1);
    },
    [shortcutsIds['10m']]: (e?: KeyboardEvent) => {
      e?.preventDefault();
      handleReschedule(10);
    },
    [shortcutsIds['1d']]: (e?: KeyboardEvent) => {
      e?.preventDefault();
      handleReschedule(60 * 24);
    },
    [shortcutsIds.flag]: (e?: KeyboardEvent) => {
      e?.preventDefault();
      handleFlag();
    },
    [shortcutsIds.doNotRepeat]: (e?: KeyboardEvent) => {
      e?.preventDefault();
      handleUnschedule();
    },
    [shortcutsIds.comment]: (e?: KeyboardEvent) => {
      e?.preventDefault();
    },
    [shortcutsIds.edit]: (e?: KeyboardEvent) => {
      e?.preventDefault();
    },
  };

  return (
    <QuizItemsContext.Provider
      value={{
        items,
        feedbackModal,
        hotkeyMap,
        hotkeyHandlers,
        setOnConfidenceKeyHandler,
        setOnCorrectnessKeyHandler,
      }}
    >
      {children}
    </QuizItemsContext.Provider>
  );
};

/**
 * Returns values of QuizItemsContext.
 */
export const useQuizItems = () => useContext(QuizItemsContext);

export default QuizItemsProvider;
